/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperProducts } from '@/scapi';

/** One entry in a breadcrumb trail, shaped like a `parentCategoryTree` record. */
export type BreadcrumbTrailEntry = Required<ShopperProducts.schemas['Category']>['parentCategoryTree'][0];

/**
 * Builds the breadcrumb trail for a category.
 *
 * SCAPI's `parentCategoryTree` is ordered root-first and **already ends with the
 * category itself**, so the trail needs no extra entry appended — doing so is what
 * duplicated the leaf in the structured data. This is the single source of truth for
 * trail composition: the visible trail and the JSON-LD `BreadcrumbList` both read it,
 * so the two cannot drift apart.
 *
 * Neither `Home` nor the product name is an entry — the trail is the primary category
 * path and nothing else.
 *
 * @param category - The category whose trail is being built
 * @param options - Composition options
 * @param options.rootCategoryId - Configured catalogue root id, filtered out of the trail
 * @returns Trail entries, ordered from the top-level category to the category itself
 */
export function buildBreadcrumbTrail(
    category: ShopperProducts.schemas['Category'],
    { rootCategoryId }: { rootCategoryId?: string } = {}
): BreadcrumbTrailEntry[] {
    const tree = category.parentCategoryTree ?? [];

    // The catalogue root is a structural node, not a shoppable destination, so it never
    // renders. Driven by config rather than a literal `'root'` so a renamed root still works.
    const trail = rootCategoryId ? tree.filter((entry) => entry.id !== rootCategoryId) : [...tree];

    // A category with no usable tree still gets a trail of itself, so a top-level
    // category page renders its own name rather than nothing.
    return trail.length > 0 ? trail : [{ id: category.id, name: category.name }];
}
